import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { UserX, RotateCcw, RefreshCw, User, Trash2, AlertTriangle } from 'lucide-react';
import { deletedPatientAPI } from '../services/api';
import { useToast, useConfirm } from './UIProvider';
import { formatClinicDate } from '../utils/clinicTime';
import SortHeader, { useSort, sortRows } from './SortHeader';
import TablePager, { usePagedRows } from './TablePager';

// Admin restore view for soft-deleted (archived) patients. Deleting a patient hides them from all
// lists/search but keeps every record; here an admin can bring one back.
const DeletedPatientsManagement = () => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const list = await deletedPatientAPI.list();
      setItems(Array.isArray(list) ? list : []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load deleted patients');
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (p) => {
    const ok = await confirm({
      title: 'Restore patient?',
      message: `Restore ${p.fullName}${p.mrn ? ` (${p.mrn})` : ''}? They'll reappear in patient lists and search.`,
      confirmText: 'Restore',
    });
    if (!ok) return;
    setBusyId(p.id);
    try {
      await deletedPatientAPI.restore(p.id);
      showToast('success', `${p.fullName || 'Patient'} restored`);
      setItems(prev => prev.filter(x => x.id !== p.id));
    } catch (e) {
      showToast('error', e.message || 'Failed to restore patient');
    } finally {
      setBusyId(null);
    }
  };

  const handlePurge = async (p) => {
    const ok = await confirm({
      title: 'Permanently delete patient?',
      subtitle: 'This cannot be undone',
      message: `Permanently delete ${p.fullName}${p.mrn ? ` (${p.mrn})` : ''} and ALL associated records - visits, prescriptions, invoices, documents and files? This is irreversible.`,
      confirmText: 'Permanently delete',
      tone: 'danger',
    });
    if (!ok) return;
    setBusyId(p.id);
    try {
      await deletedPatientAPI.purge(p.id);
      showToast('success', `${p.fullName || 'Patient'} permanently deleted`);
      setItems(prev => prev.filter(x => x.id !== p.id));
    } catch (e) {
      showToast('error', e.message || 'Failed to permanently delete patient');
    } finally {
      setBusyId(null);
    }
  };

  // Flagged (past retention) rows first.
  const rows = (items || []).slice().sort((a, b) => (b.flaggedForReview ? 1 : 0) - (a.flaggedForReview ? 1 : 0));
  // Flagged-for-review floats to the top until a column is picked, at which point the chosen
  // order wins outright. The amber row still marks those out, so nothing is lost by letting it.
  const { sortConfig, handleSort } = useSort();
  const sortedRows = useMemo(() => sortRows(rows, sortConfig, {
    patient:   (p) => p.fullName,
    phone:     (p) => p.phoneNumber,
    deletedBy: (p) => p.deletedByName,
  }), [rows, sortConfig]);
  const flaggedCount = rows.filter(r => r.flaggedForReview).length;

  const paging = usePagedRows(sortedRows, { id: 'deleted-patients', resetOn: [sortConfig] });

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <UserX className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Deleted Patients</h1>
                <p className="text-gray-600">Archived patients - their records are kept; restore to make them visible again</p>
              </div>
            </div>
            <button onClick={load} className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50" title="Refresh">
              <RefreshCw className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg text-red-700">{error}</div>}

        {flaggedCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-2.5 mb-4 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {flaggedCount} patient{flaggedCount === 1 ? '' : 's'} archived past the retention period - review, then restore or permanently delete.
          </div>
        )}

        {items === null ? (
          <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <UserX className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-500">No deleted patients</h3>
            <p className="text-gray-400 text-sm mt-1">Patients deleted by staff will appear here for restore.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <TablePager paging={paging} noun="deleted patient" />
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <SortHeader label="Patient" sortKey="patient" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                  <SortHeader label="Phone" sortKey="phone" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                  <SortHeader label="Deleted by" sortKey="deletedBy" sortConfig={sortConfig} onSort={handleSort} className="px-6 py-3" />
                  <th className="text-right px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paging.pageRows.map((p) => (
                  <tr key={p.id} className={p.flaggedForReview ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50/60'}>
                    <td className="px-6 py-3">
                      <div className="font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
                        <User className="h-3.5 w-3.5 text-gray-400" />{p.fullName || '—'}
                        {p.flaggedForReview && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                            <AlertTriangle className="h-3 w-3" /> Review · past retention
                          </span>
                        )}
                      </div>
                      {p.mrn && <div className="text-xs text-blue-600 font-mono">{p.mrn}</div>}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">{p.phoneNumber || '—'}</td>
                    <td className="px-6 py-3">
                      <div className="text-sm text-gray-800">{p.deletedByName || '—'}</div>
                      {p.deletedAt && (
                        <div className="text-xs text-gray-400">{formatClinicDate(p.deletedAt, { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {p.mergedIntoMrn ? (
                          // Nothing is left on a merged file: its records moved to the kept one, so
                          // there is nothing to restore, only the old MRN still pointing there.
                          <span className="text-xs text-gray-500 whitespace-nowrap" title="Merged files cannot be restored on their own">
                            Merged into <span className="font-mono">{p.mergedIntoMrn}</span>
                          </span>
                        ) : (
                          <button
                            onClick={() => handleRestore(p)}
                            disabled={busyId === p.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50"
                          >
                            {busyId === p.id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Restore
                          </button>
                        )}
                        <button
                          onClick={() => handlePurge(p)}
                          disabled={busyId === p.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                          title="Permanently delete patient + all records (irreversible)"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <TablePager paging={paging} noun="deleted patient" position="bottom" />
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletedPatientsManagement;
